'use client';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useMemo, useState, useEffect, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { WalletEntrance, WalletCustomParams } from '../../types';
import IconFont from '@/components/ui/iconFont';

// 钱包组件配置
const WALLET_COMPONENTS = {
  'root': {
    component: dynamic(() => import('../wallet-root')),
  },
  'deposit-crypto': {
    component: dynamic(() => import('../deposit-crypto')),
  },
  'deposit-exchange': {
    component: dynamic(() => import('../deposit-exchange')),
  },
  'deposit-usdt': {
    component: dynamic(() => import('../deposit-usdt')),
  },
  'deposit-cash': {
    component: dynamic(() => import('../deposit-cash')),
  },
  'withdraw': {
    component: dynamic(() => import('../withdraw')),
  }
} as const;

export interface WalletModalProps {
  entrance?: WalletEntrance;
  customTitle?: string;
  customDesc?: string | ReactNode;
  hideWithdraw?: boolean;
  hideRecord?: boolean;
  customParams?: WalletCustomParams;
}

const WalletModal = NiceModal.create<WalletModalProps>(({ entrance = 'root', customTitle, customDesc, hideWithdraw = false, customParams }) => {
  const t = useTranslations('recharge');
  const modal = useModal();
  const [currentEntrance, setCurrentEntrance] = useState(entrance);

  // 监听 entrance 参数的变化，当外部传入新的 entrance 时更新内部状态
  useEffect(() => {
    setCurrentEntrance(entrance);
  }, [entrance]);

  // 当模态框隐藏时，重置为初始的 entrance 值
  useEffect(() => {
    if (!modal.visible) {
      setCurrentEntrance(entrance);
    }
  }, [modal.visible, entrance]);

  // 当前组件信息
  const currentWallet = useMemo(() => {
    return WALLET_COMPONENTS[currentEntrance];
  }, [currentEntrance]);

  // 切换钱包模块
  const switchWalletModule = useCallback((newEntrance: WalletEntrance) => {
    setCurrentEntrance(newEntrance);
  }, []);

  // 隐藏钱包
  const hideWallet = useCallback(() => {
    modal.hide();
  }, [modal]);

  const WalletComponent = currentWallet.component;

  return (
    <Dialog.Root open={modal.visible} onOpenChange={(open) => !open && hideWallet()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-[fadeIn_300ms_ease-out] data-[state=closed]:animate-[fadeOut_300ms_ease-in]" />
        <Dialog.Content
          className="fixed bottom-0 left-0 right-0 z-50 w-full rounded-t-[8px] pt-[25px] pb-[30px] px-3 overflow-hidden data-[state=open]:animate-[slide-in-from-bottom_300ms_ease-out] data-[state=closed]:animate-[slide-out-to-bottom_300ms_ease-in] [animation-fill-mode:forwards]"
          style={{
            background: 'linear-gradient(160deg, #192A42 0%, #181D29 100%)',
            WebkitTransform: 'translateZ(0)',
            transform: 'translateZ(0)'
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="text-[17px] text-white leading-3 mb-5 relative flex items-center justify-center">
            <span className="text-base font-semibold text-primary">{customTitle || t("title")}</span>

            <Dialog.Close className="cursor-pointer absolute right-[8px] before:content-[''] before:absolute before:w-[34px] before:h-[34px] before:-left-[10.5px] before:-top-[10.5px]" onClick={hideWallet}>
              <IconFont name="close" color='#a9b5cb' size={13} />
            </Dialog.Close>
          </Dialog.Title>
          {customDesc && <div className="font-[Montserrat] text-[11px] text-secondary flex justify-center items-center mb-[20px]">{customDesc}</div>}
          <WalletComponent
            entrance={currentEntrance}
            initialEntrance={entrance}
            onSwitchModule={switchWalletModule}
            onHide={hideWallet}
            hideWithdraw={hideWithdraw}
            customParams={customParams}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

WalletModal.displayName = 'WalletModal';

export default WalletModal;
